import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select } from 'antd';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useParams } from 'react-router';
import PropTypes from 'prop-types';
import { FolderOutlined } from '@ant-design/icons';

import { createFolder, updateFolder, fetchFolders } from 'state/actions/folders';
import { log } from 'utils';
import { regexInput } from '../../utils/regex';
import './ModalFolderForm.scss';

const { Option } = Select;

export default function ModalFolderForm({
  folder,
  setFolder,
  visible,
  onCancel,
  isEditing,
}) {
  const dispatch = useDispatch();
  const { siteId } = useParams();
  const [form] = Form.useForm();
  const [errors, setErrors] = useState({ name: '' });

  const { foldersList, loading } = useSelector(
    (state) => ({
      foldersList: state.folders.list,
      loading: state.folders.loading,
    }),
    shallowEqual
  );

  useEffect(() => {
    // Fetch folders when component mounts
    if (siteId) {
      dispatch(fetchFolders({ siteId }));
    }
  }, [dispatch, siteId]);

  const onSave = async () => {
    const { name, parentId } = folder;
    const nameValid = regexInput(name);

    if (!nameValid && name) {
      setErrors({
        name: 'This field cannot contain any special characters',
      });
      return;
    }

    if (!name || !name.trim()) {
      setErrors({
        name: 'Folder name is required',
      });
      return;
    }

    setErrors({ name: '' });

    try {
      if (isEditing) {
        await dispatch(updateFolder({
          id: folder._id,
          name: name.trim(),
          parentId: parentId || null,
          siteId,
        }));
      } else {
        await dispatch(createFolder({ 
          name: name.trim(), 
          parentId: parentId || null, 
          siteId 
        }));
      }
      
      form.resetFields();
      onCancel();
    } catch (error) {
      console.error('Failed to save folder:', error);
    }
  };

  const onChangeHandler = (e) => {
    const { name, value } = e.target;
    setFolder((prev) => ({ ...prev, [name]: value }));
  };

  const onParentChange = (value) => {
    setFolder((prev) => ({ ...prev, parentId: value }));
  };

  // Get folder path for display
  const getFolderPath = (folderId) => {
    const folderItem = foldersList.find(f => f._id === folderId);
    if (!folderItem) return '';
    
    if (folderItem.ancestorNames && folderItem.ancestorNames.length > 0) {
      return `${folderItem.ancestorNames.join(' / ')} / ${folderItem.name}`;
    }
    return folderItem.name;
  };

  // Filter out current folder and its descendants when editing
  const getAvailableParentFolders = () => {
    if (!isEditing) return foldersList;
    
    return foldersList.filter(f => {
      // Don't allow selecting self as parent
      if (f._id === folder._id) return false;
      
      // Don't allow selecting descendants as parent
      if (f.ancestors && f.ancestors.includes(folder._id)) return false;
      
      return true;
    });
  };

  return (
    <Modal
      className="adsign-modal folder-form"
      open={visible}
      title={isEditing ? 'Edit Folder' : 'Create New Folder'}
      okText="Submit"
      confirmLoading={loading}
      onCancel={() => {
        onCancel();
        form.resetFields();
      }}
      footer={
        <div>
          <button
            className="button-danger-secondary mr-2"
            onClick={() => {
              onCancel();
              form.resetFields();
            }}
          >
            Cancel
          </button>
          <button
            className="button-primary"
            onClick={() => {
              form
                .validateFields()
                .then(() => onSave())
                .catch((info) => log('Validate Failed:', info));
            }}
          >
            {isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      }
    >
      <Form
        className="form"
        layout="vertical"
        form={form}
        initialValues={{
          name: folder.name || '',
          parentId: folder.parentId || null,
        }}
      >
        <Form.Item
          label="Folder Name"
          name="name"
          rules={[{ required: true, message: 'Please enter folder name' }]}
          validateStatus={errors.name ? 'error' : 'success'}
          help={errors.name || null}
        >
          <Input 
            name="name" 
            value={folder.name || ''} 
            onChange={onChangeHandler}
            placeholder="Enter folder name"
          />
        </Form.Item>

        <Form.Item
          label="Parent Folder (optional)"
          name="parentId"
          help="Leave empty to create folder at root level"
        >
          <Select
            value={folder.parentId}
            onChange={onParentChange}
            placeholder="Select parent folder or leave empty for root"
            allowClear
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {getAvailableParentFolders().map(folderItem => (
              <Option key={folderItem._id} value={folderItem._id}>
                <FolderOutlined /> {getFolderPath(folderItem._id)}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
}

ModalFolderForm.propTypes = {
  folder: PropTypes.object.isRequired,
  setFolder: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  isEditing: PropTypes.bool,
};